import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
	LocalizationProvider,
	MobileDateTimePicker,
} from '@mui/x-date-pickers';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useActiveVehicle } from '../../context/ActiveVehicleProvider';
import { useNotificationProvider } from 'hooks';
import TicketAttachments from '../ticketAttachments/ticketAttachments';
import AttachmentImageGallery from '../attachmentImageGallery/AttachmentImageGallery';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import { generateTicketNumber } from '../../utils/generateTicketNumber';
import { useTicketTypeMeta } from '../../context/TicketTerminologyProvider';
import { submitTicket } from '../../utils/submitTicket';
import { Ticket } from '../../types/ticket';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import AddIcon from '@mui/icons-material/Add';
import VinScanningComponent from './VinScanningComponent';
import ChangeTicketTypeMenu from './ChangeTicketTypeMenu';
import CompactViolationHistory from './CompactViolationHistory';
import ScannedPlateHeader from '../scannedPlateHeader/ScannedPlateHeader';
import { getNextViolationTicketType } from '../../utils/getNextViolationTicketType';
import { getHourlyTimeContext } from '../../utils/getHourlyTimeContext';
import { normalizePhotoOrientation } from '../../utils/normalizePhotoOrientation';
import { formatVehicleInfoFromVin } from '../../utils/formatVehicleInfoFromVin';
import { getLatestVehicleInfo } from '../../utils/getLatestVehicleInfo';
import { VinVehicleInfo } from 'api/canpark/scanning/extractVin';


const TicketForm: React.FC = () => {
	const [submitting, setSubmitting] = React.useState(false);
	const {
		currentSearch,
		setStage,
		goNext,
		goBack,
		closeDrawer,
		tickets,
		inWindowTickets,
		ticketFormMethods,
		uppyTicket,
		openModal,
		setOpenModal,
		parkingLocation,
		parkingAuthorization,
		vin,
		vinVehicleInfo,
		setVin,
		setVinVehicleInfo,
		isVinCameraOn,
		setIsVinCameraOn,
	} = useActiveVehicle();
	const { userInfo } = useSelector(selectUserInfo);
	const getTicketTypeMeta = useTicketTypeMeta();

	const {
		control,
		setValue,
		formState: { errors },
	} = ticketFormMethods;

	const { notifyError, notifySuccess } = useNotificationProvider();

	const skipConfirm = parkingLocation?.showConfirmStage === false;
	// When the action stage is hidden, the operator never sees the violation
	// history or the Record / Warning / Tow buttons. Surface that context
	// inline on the ticket form so they can still adjust if the auto-set
	// ticketType is wrong.
	const showActionContext = parkingLocation?.showActionStage === false;

	useEffect(() => {
		if (currentSearch) {
			setValue('parkingLocationId', currentSearch.parkingLocation.id);
			setValue('licenseNumber', currentSearch.licenseNumber);
			setValue('licenseState', currentSearch.licenseState);
		}
		
		// Prioritize VIN-decoded vehicle info over historical tickets
		if (vinVehicleInfo) {
			const formattedVehicleInfo = formatVehicleInfoFromVin(vinVehicleInfo);
			if (formattedVehicleInfo) {
				setValue('vehicleInfo', formattedVehicleInfo);
			}
		} else {
			// Seed from the most recent meaningful description across the plate's
			// FULL history (not just the lookup window), so a known vehicle
			// description persists even after older tickets age out of the window.
			setValue('vehicleInfo', getLatestVehicleInfo(tickets));
		}
		
		setValue('ticketNumber', generateTicketNumber());
		setValue('issuedBy', userInfo.fullname);
		setValue('date', new Date());
	}, [currentSearch, setValue, vinVehicleInfo, tickets]); // Add dependencies here

	useEffect(() => {
		// Set the default value for parkingZone to the first item in parkingZoneOptions
		if ((parkingLocation?.parkingZoneOptions ?? []).length > 0) {
			setValue('parkingZone', parkingLocation?.parkingZoneOptions?.[0]?.name || '');
		}
	}, [parkingLocation, setValue]);

	const onError = (errors: unknown) => {
		console.warn('[TicketForm] Validation errors:', errors);
		notifyError('Please fill out all required fields');
	};

	const runSubmit = async (data: Ticket) => {
		setSubmitting(true);
		try {
			await submitTicket(data, {
				userId: userInfo.userId,
				parkingLocation,
				notifySuccess,
				notifyError,
				resetForm: () => ticketFormMethods.reset(),
				setStage,
				closeDrawer,
			});
		} finally {
			setSubmitting(false);
		}
	};

	const onSubmit = (data: Ticket) => {
		if (skipConfirm) {
			void runSubmit(data);
		} else {
			goNext();
		}
	};

	const attachments = ticketFormMethods.watch('attachments');

	const ticketType = ticketFormMethods.watch('ticketType');
	const licenseNumber = ticketFormMethods.watch('licenseNumber');
	const licenseState = ticketFormMethods.watch('licenseState');

	const { textColor: ticketTypeTextColor, shortLabel: ticketTypeActionText } =
		getTicketTypeMeta(ticketType);

	const hourlyTimeContext = parkingLocation
		? getHourlyTimeContext({
				parkingLocation,
				tickets: inWindowTickets,
				getTicketTypeMeta,
		  })
		: null;

	const fileInputRef = React.useRef<HTMLInputElement | null>(null);

	const handlePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file || !uppyTicket) return;

		try {
			const normalized = await normalizePhotoOrientation(file);
			const uniqueName = `${Date.now()}_${normalized.name || 'photo.jpg'}`;

			await uppyTicket.addFile({
				name: uniqueName,
				type: normalized.type || 'image/jpeg',
				data: normalized,
				source: 'camera',
				meta: {
					relativePath: uniqueName,
				},
			});
			// Open the Uppy modal so the user can confirm/upload
			setOpenModal(true);
		} catch (err) {
			console.error('[TicketForm] Failed to add photo:', err);
			notifyError('Failed to add photo. Please try again.');
		}
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const takePhoto = () => {
		fileInputRef.current?.click();
	};

	const handleVinScanned = (
		scannedVin: string,
		vehicleInfo: VinVehicleInfo | null,
	) => {
		setVin(scannedVin);
		setVinVehicleInfo(vehicleInfo);
		setValue('vin', scannedVin);

		if (vehicleInfo) {
			const formattedVehicleInfo = formatVehicleInfoFromVin(vehicleInfo);
			if (formattedVehicleInfo) {
				setValue('vehicleInfo', formattedVehicleInfo);
			}
		}
	};

	return (
		<Box
			sx={{
				overflowY: 'auto',
				width: '100%',
				display: 'flex',
				justifyContent: 'center',
			}}
		>
			<Box
				sx={{
					width: '100%',
					maxWidth: 600,
					px: 2,
					pb: 2,
				}}
			>
				<Typography
					variant="h6"
					fontWeight={500}
					gutterBottom
				>
					Save a record at{' '}
					<Typography
						color="primary"
						component="span"
						variant="h6"
						fontWeight={500}
					>
						{parkingLocation?.name}
					</Typography>
				</Typography>
				<Box pb={1}>
					<ScannedPlateHeader
						variant="banner"
						licenseNumber={licenseNumber}
						licenseState={licenseState}
					/>
				</Box>
				<Box py={1}>
					<Box
						display="flex"
						alignItems="center"
						gap={1}
						flexWrap="wrap"
					>
						<Typography variant="body2">
							<Typography
								component="span"
								variant="body2"
								color="text.secondary"
								fontStyle="italic"
							>
								Record Type:{' '}
							</Typography>
							<Typography
								component="span"
								variant="body2"
								color={ticketTypeTextColor}
								fontWeight={500}
							>
								{ticketTypeActionText}
							</Typography>
						</Typography>
						{showActionContext && <ChangeTicketTypeMenu />}
					</Box>
					{showActionContext && parkingLocation && (() => {
						const decision = getNextViolationTicketType({
							tickets: inWindowTickets,
							parkingLocation,
						});
						const isHourly =
							parkingLocation.violationCheckType === 'hourlyExpiry';

						let headline: string;
						let severityColor: string;
						let description: string | null = null;

						if (parkingAuthorization?.allowed) {
							headline = 'No action required';
							severityColor = 'primary.main';
						} else if (decision.action === 'withinLimit') {
							headline = 'Within time limit';
							severityColor = 'success.main';
						} else if (decision.action === 'sessionClosed') {
							headline = 'Already actioned this session';
							severityColor = 'text.secondary';
						} else {
							const meta = getTicketTypeMeta(decision.ticketType);
							severityColor = meta.textColor;
							description = meta.description;
							if (isHourly) {
								headline =
									decision.ticketType === 'log'
										? `${meta.label} — start of session`
										: `Time limit exceeded — ${meta.label.toLowerCase()}`;
							} else {
								headline = meta.label;
							}
						}

						return (
							<Paper
								variant="outlined"
								sx={{
									mt: 1,
									p: 1.5,
									borderRadius: 2,
									borderColor: severityColor,
									borderWidth: 1.5,
									borderLeftWidth: 5,
								}}
							>
								<Typography
									variant="overline"
									color="text.secondary"
									sx={{
										display: 'block',
										lineHeight: 1.2,
										letterSpacing: 0.6,
									}}
								>
									Reason
								</Typography>
								<Typography
									variant="body1"
									fontWeight={600}
									color={severityColor}
									sx={{ mt: 0.25 }}
								>
									{headline}
								</Typography>
								{description && (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mt: 0.5 }}
									>
										{description}
									</Typography>
								)}
								{hourlyTimeContext && (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mt: 0.5 }}
									>
										{hourlyTimeContext}
									</Typography>
								)}
							</Paper>
						);
					})()}
				</Box>
				{!isVinCameraOn && (
					<Box py={1} display="flex" gap={1} flexWrap="wrap">
						<Button
							data-tour="ticket-attach"
							startIcon={<AttachFileIcon />}
							variant="outlined"
							color="primary"
							onClick={() => setOpenModal(true)}
						>
							Attach Image
						</Button>
						<Button
							startIcon={<CameraAltIcon />}
							variant="outlined"
							color="secondary"
							onClick={takePhoto}
						>
							Take Photo
						</Button>
						<Button
							data-tour="ticket-vin"
							startIcon={<QrCodeScannerIcon />}
							variant="outlined"
							color="secondary"
							onClick={() => setIsVinCameraOn(true)}
						>
							Scan VIN
						</Button>
						<input
							type="file"
							accept="image/*"
							capture="environment"
							style={{ display: 'none' }}
							ref={fileInputRef}
							onChange={handlePhotoChange}
						/>
					</Box>
				)}
				<VinScanningComponent
					onVinScanned={handleVinScanned}
					isCameraOn={isVinCameraOn}
					setIsCameraOn={setIsVinCameraOn}
				/>
				{attachments.length > 0 && (
					<Box mt={2} mb={2} display="flex" flexDirection="column" gap={1}>
						<AttachmentImageGallery attachments={attachments} />
						<TicketAttachments
							attachments={ticketFormMethods.getValues(
								'attachments',
							)}
							setAttachments={(attachments) => {
								setValue('attachments', attachments);
							}}
						/>
					</Box>
				)}

				{showActionContext && (
					<Box mt={1} mb={1}>
						<CompactViolationHistory />
					</Box>
				)}

				<form
					data-tour="ticket-form"
					onSubmit={ticketFormMethods.handleSubmit(onSubmit, onError)}
				>
					<Box
						display="flex"
						flexDirection="column"
						gap={2}
						width="100%"
						py={1}
					>
						<Controller
							name="parkingZone"
							control={control}
							rules={{ required: 'Zone is required' }}
							render={({ field }) => (
								<Autocomplete
									freeSolo
									options={
										parkingLocation?.parkingZoneOptions.map(
											(zone) => zone.name,
										) || []
									}
									inputValue={field.value || ''}
									onInputChange={(_, newInputValue) =>
										field.onChange(newInputValue)
									}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Parking Zone/Address"
											fullWidth
											error={!!errors.parkingZone}
											helperText={
												errors.parkingZone?.message ||
												'Either select from the dropdown or enter a custom zone/address.'
											}
										/>
									)}
								/>
							)}
						/>

						<Controller
							name="date"
							control={control}
							rules={{ required: 'Date and Time are required' }}
							render={({ field }) => (
								<LocalizationProvider
									dateAdapter={AdapterDateFns}
								>
									<MobileDateTimePicker
										value={field.value}
										onChange={(newValue) =>
											field.onChange(newValue)
										}
										label="Date and Time"
										inputFormat="MM/dd/yyyy hh:mm a"
										renderInput={(params) => (
											<TextField
												{...params}
												fullWidth
												error={!!errors.date}
												helperText={
													errors.date?.message
												}
											/>
										)}
									/>
								</LocalizationProvider>
							)}
						/>

						<Controller
							name="vehicleInfo"
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									data-tour="ticket-vehicle-info"
									label="Vehicle Info (optional)"
									placeholder="Make/Model/Color e.g. Honda Accord 2020 Black"
									fullWidth
									error={!!errors.vehicleInfo}
									helperText={errors.vehicleInfo?.message}
								/>
							)}
						/>

						{vin && (
							<Box>
								<TextField
									label="VIN"
									value={vin}
									fullWidth
									InputProps={{
										readOnly: true,
									}}
									sx={{
										'& .MuiInputBase-root': {
											backgroundColor: vinVehicleInfo
												? 'action.selected'
												: 'warning.light',
										},
									}}
								/>
								<Button
									size="small"
									color="error"
									onClick={() => {
										setVin(null);
										setVinVehicleInfo(null);
										setValue('vin', undefined);
									}}
									sx={{ mt: 0.5 }}
								>
									Clear VIN
								</Button>
							</Box>
						)}

						<Controller
							name="comments"
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									data-tour="ticket-comments"
									label="Additional Comments (Optional)"
									fullWidth
									multiline
									minRows={3}
								/>
							)}
						/>
						<Box
							sx={{
								width: '100%',
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								gap: 2,
								flexWrap: 'wrap',
							}}
						>
							<Button
								variant="outlined"
								color="primary"
								startIcon={<ArrowBackIosNewIcon />}
								onClick={goBack}
								disabled={submitting}
							>
								Back
							</Button>
							{skipConfirm ? (
								<Box
									display="flex"
									gap={2}
									flexWrap="wrap"
									sx={{ ml: 'auto' }}
								>
									<Tooltip
										title={submitting ? 'Submitting…' : ''}
									>
										<span>
											<Button
												variant="contained"
												type="submit"
												color="primary"
												data-tour="ticket-save"
												endIcon={
													submitting ? (
														<CircularProgress
															size={16}
															color="inherit"
														/>
													) : (
														<AddIcon />
													)
												}
												disabled={submitting}
												sx={{
													borderRadius: 2,
													fontWeight: 500,
													textTransform: 'none',
												}}
											>
												Save
											</Button>
										</span>
									</Tooltip>
								</Box>
							) : (
								<Button
									variant="contained"
									type="submit"
									color="primary"
									data-tour="ticket-save"
									endIcon={<ArrowForwardIosIcon />}
									sx={{
										borderRadius: 2,
										fontWeight: 500,
										textTransform: 'none',
										ml: 'auto',
									}}
								>
									Next
								</Button>
							)}
						</Box>
					</Box>
				</form>
			</Box>
		</Box>
	);
};

export default TicketForm;
